// @ts-strict-ignore
import CartItemRow from './CartItemRow'
import LoadingRow from './LoadingRow'
import { useLocalization } from '@components/Localization'
import { useNotifications } from '@components/NotificationsProvider'
import { Box, theme } from '@gassan-ui'
import { CartItem, useAddGiftcardToCartMutation, useUpdateCartItemSizeMutation } from '@generated'
import { genericPushToTrackingLayer } from '@lib/data-layer-social-ads/generic-push-to-tracking-layer'
import { cartItemsToDataLayerItems } from '@lib/data-layer-social-ads/helpers/cartItemsToDataLayerItems'
import { dataLayerViewCart } from '@lib/data-layer-social-ads/view-cart'
import { useAddArticleToCart } from '@lib/hooks/use-add-article-to-cart'
import { useRemoveItemFromCart } from '@lib/hooks/use-remove-item-from-cart'
import { useCart } from '@lib/hooks/useCart'
import pinterestTracking from '@lib/pinterest-tracking'
import { GIFTCARD_ID } from '@pages/giftcard'
import { AnimatePresence } from 'framer-motion'
import { useTranslation } from 'next-i18next'
import { FC, useEffect } from 'react'

type Props = {
  items: CartItem[]
  loadingRow: string | null
  handleChangeQuantity: (cartItemId: string, quantity: number) => void
}

const CartItemList: FC<Props> = ({ handleChangeQuantity, loadingRow, items }) => {
  const { parseGraphQlError } = useLocalization()
  const { t } = useTranslation(['notifications', 'other'])
  const notifications = useNotifications()
  const cart = useCart()
  const reference = cart?.id

  const [, updateCartItemSize] = useUpdateCartItemSizeMutation()
  const { removeItemFromCart } = useRemoveItemFromCart()
  const { handleAddArticleToCart } = useAddArticleToCart()
  const [, addGiftcardToCart] = useAddGiftcardToCartMutation()

  const handleUpdateSize = async (cartItemId: string, size: string) => {
    try {
      await updateCartItemSize({ reference, cartItemId, size })
    } catch (error) {
      console.error(error)
    }
  }

  const handleRemoveItemFromCart = async (
    cartItemId: string,
    articleId: string,
    quantity: number,
  ) => {
    if (!cart) {
      return
    }

    if (articleId === GIFTCARD_ID) {
      handleRemoveGiftcardFromCart(cartItemId, quantity)
    } else {
      removeItemFromCart(cartItemId)
    }
  }

  useEffect(() => {
    if (cart && cart.items && cart.totalPriceExTax) {
      const items = cartItemsToDataLayerItems(cart.items)
      dataLayerViewCart(cart.totalPriceExTax, items)
    }
  }, [cart])

  const handleAddArticleOrGiftcardToCart = async ({
    articleId,
    quantity,
    size,
    amount,
  }: {
    articleId: string
    quantity: number
    size?: string
    amount?: number
  }) => {
    // if 'amount' is passed, assume giftcard
    if (amount) {
      try {
        const { data, error } = await addGiftcardToCart({
          reference,
          amount,
          quantity,
        })

        if (error) {
          return notifications.addNotification({
            variant: 'error',
            label: parseGraphQlError(error.message),
          })
        }

        if (data) {
          genericPushToTrackingLayer('Ecommerce', 'addToCart')
          pinterestTracking.addToCart({
            product_id: articleId,
            product_quantity: quantity,
          })
        }
      } catch (error) {
        notifications.addNotification({
          variant: 'error',
          label: t('refreshAndTryAgain', { ns: 'notifications' }),
        })
      }
    } else {
      handleAddArticleToCart({
        articleId,
        quantity,
        size,
      })
    }
  }

  return (
    <Box
      pt={{ _: '1rem', small: '1.75rem', xlarge: '2rem' }}
      borderTop={`1px solid ${theme.colors.shades[200]}`}
    >
      {items ? (
        <AnimatePresence>
          {items.map((item, key) => {
            return (
              <CartItemRow
                key={`${item.id}-${key}`}
                index={key}
                item={item}
                variant={!cart ? 'loading' : loadingRow === item.id ? 'updating' : 'idle'}
                onChangeQuantity={(quantity) => handleChangeQuantity(item.id, quantity)}
                onChangeSize={(size) => handleUpdateSize(item.id, size)}
                onRemove={() => handleRemoveItemFromCart(item.id, item.articleId, item.quantity)}
              />
            )
          })}
        </AnimatePresence>
      ) : (
        [...Array(2)].map((_, key) => <LoadingRow key={key} />)
      )}
    </Box>
  )

  async function handleRemoveGiftcardFromCart(cartItemId: string, quantity: number) {
    if (!cart) {
      return
    }
    const item = cart.items.find((item) => item.id === cartItemId)
    try {
      await removeItemFromCart(cartItemId)
      notifications.addNotification({
        variant: 'icon-action-undo',
        label: item.article.brand,
        sublabel: item.article.description,
        description: t('removedFromShoppingCart', { ns: 'other' }),
        actionLabel: t('undo', { ns: 'other' }),
        onClickAction: () =>
          handleAddArticleOrGiftcardToCart({
            articleId: item.article.id,
            quantity: item.quantity,
            amount: item.unitPrice / 100,
          }),
      })
      genericPushToTrackingLayer('Ecommerce', 'removeFromCart')
    } catch (error) {
      notifications.addNotification({
        variant: 'error',
        label: t('refreshAndTryAgain', { ns: 'notifications' }),
      })
    }
  }
}

export default CartItemList
